import React, { useEffect, useState } from 'react';
import { Home, Info, Lock, FileText, ShieldCheck, ClipboardList, ArrowDown, X, Users, Smartphone, RotateCw } from 'lucide-react';
import PVSPageLayout from '../PVSPageLayout';
import DocumentArchiveButton from '../DocumentArchiveButton';
import CalorVesselsNotice from './CalorVesselsNotice';
import { canAccess } from '../../config/userPermissions';
import { getResponsiblePersonnel } from '../../data/trainingMatrix';

// Maps internal doc keys to their display labels and the type badge shown
// in the expanded process card. Doc keys must mirror Documents.js DOC_REGISTRY
// or PVSForms.js FORM_CATALOG. Add a key to a node's `links` array (below) to
// render the linked tile.
const DOC_LIBRARY = {
  // Policies
  wse: { label: 'Written Scheme of Examination', type: 'Policy', target: 'doc' },
  hsafety: { label: 'Health & Safety Policy', type: 'Policy', target: 'doc' },
  COSHH: { label: 'COSHH', type: 'Policy', target: 'doc' },
  ImpartialityConfidentialityDoc: { label: 'Impartiality & Confidentiality Agreement', type: 'Policy', target: 'doc' },
  VesselTaggingPolicyDoc: { label: 'Vessel Tagging & Digital Application Policy', type: 'Policy', target: 'doc' },
  // RAMS
  vesselprep: { label: 'Vessel Inspection and Preparation', type: 'RAMS', target: 'doc' },
  PortableCompressorUnitDoc: { label: 'Transfer of Liquid LPG (PCU)', type: 'RAMS', target: 'doc' },
  pryolysis: { label: 'Pyrolysis', type: 'RAMS', target: 'doc' },
  shotblasting: { label: 'Shotblasting', type: 'RAMS', target: 'doc' },
  spraybooth: { label: 'Spraybooth', type: 'RAMS', target: 'doc' },
  liftingtransport: { label: 'Lifting Operations and Transport', type: 'RAMS', target: 'doc' },
  vesselairtest: { label: 'Vessel Air Test', type: 'RAMS', target: 'doc' },
  ForkLiftTruckDoc: { label: 'Fork Lift Truck', type: 'RAMS', target: 'doc' },
  ManualHandlingDoc: { label: 'Manual Handling', type: 'RAMS', target: 'doc' },
  WeldingDoc: { label: 'Welding, Plasma Cutting & Oxy-Gas', type: 'RAMS', target: 'doc' },
  PillarDrillDoc: { label: 'Pillar Drill', type: 'RAMS', target: 'doc' },
  BenchGrinderDoc: { label: 'Bench Grinder', type: 'RAMS', target: 'doc' },
  GuillotineDoc: { label: 'Metal Cutting Guillotine', type: 'RAMS', target: 'doc' },
  PortableHandToolsDoc: { label: 'Portable Hand Tools', type: 'RAMS', target: 'doc' },
  JetWashPressureWasherDoc: { label: 'Jet Wash / Pressure Washers', type: 'RAMS', target: 'doc' },
  // Risk Assessments
  OnSiteRA: { label: 'On Site Risk Assessment', type: 'RA', target: 'doc' },
  WorkplaceTransportDoc: { label: 'Workplace Transport', type: 'RA', target: 'doc' },
  DrivingGoodsVehiclesDoc: { label: 'Driving Goods Vehicles', type: 'RA', target: 'doc' },
  DriverThirdPartySitesDoc: { label: 'Driver Attending Third Party Sites', type: 'RA', target: 'doc' },
  LoneWorkingDoc: { label: 'Lone Working – Pyrolysis / Shot Blasting', type: 'RA', target: 'doc' },
  OvergroundTankBasesDoc: { label: 'Production of Bases for Overground Tanks', type: 'RA', target: 'doc' },
  // SSOW
  AccessingTrailersDoc: { label: 'Accessing Trailers', type: 'SSOW', target: 'doc' },
  FireGeneralSafetyDoc: { label: 'General Fire Safety', type: 'SSOW', target: 'doc' },
  FLTChangingLPGCylindersDoc: { label: 'Changing LPG Cylinders (Forklifts)', type: 'SSOW', target: 'doc' },
  LaddersStepladdersDoc: { label: 'Ladders and Stepladders', type: 'SSOW', target: 'doc' },
  OvensDoc: { label: 'Drying Ovens', type: 'SSOW', target: 'doc' },
  PortableGasSpaceHeatersDoc: { label: 'Portable Gas Space Heaters', type: 'SSOW', target: 'doc' },
  SlingsChainsLiftingDoc: { label: 'Slings, Chains and Lifting Appliances', type: 'SSOW', target: 'doc' },
  // Forms
  'tank-movement': { label: 'Tank Movement Form', type: 'Form', target: 'form' },
  'tank-certification': { label: 'Tank Certification', type: 'Form', target: 'form' },
  'final-qc-checklist': { label: 'Final QC Checklist', type: 'Form', target: 'form' },
  engineering: { label: 'Engineering Form', type: 'Form', target: 'form' },
  'pre-inspection': { label: 'Pre Inspection Form', type: 'Form', target: 'form' },
  'paint-quality-inspection': { label: 'Paint Quality Inspection', type: 'Form', target: 'form' },
  'pre-coating-batch': { label: 'Pre Coating Batch Quality', type: 'Form', target: 'form' },
  'vessel-failure-report': { label: 'Vessel Failure Report', type: 'Form', target: 'form' },
  'customer-complaint': { label: 'Customer Complaint & Appeal', type: 'Form', target: 'form' },
};

// Tailwind class fragments per fill colour.  Used by ProcessNode for the
// header button and by the expanded body's border/background.
const FILL_STYLES = {
  orange: {
    container: 'border-orange-400/70 bg-orange-500/20',
    header: 'hover:bg-orange-500/30',
  },
  green: {
    container: 'border-emerald-400/70 bg-emerald-500/20',
    header: 'hover:bg-emerald-500/30',
  },
  red: {
    container: 'border-red-400/70 bg-red-500/20',
    header: 'hover:bg-red-500/30',
  },
  purple: {
    container: 'border-purple-400/70 bg-purple-500/20',
    header: 'hover:bg-purple-500/30',
  },
};

// Process map nodes mirrored 1:1 from PVS-VRPM-001 (Vessel Refurbishment
// Process Map). Titles and order are taken directly from the PDF — do not
// re-order or re-word. `description`, `roles` and `links` are intentionally
// left empty for nodes whose linked documents / responsible personnel are
// still to be confirmed; populate them as the information is provided.
//
// A phase's `rows` array can contain two row types:
//   - { type: 'single', node, arrowBelow? }
//       full-width node; arrowBelow defaults to true unless it is the last
//       row in the phase
//   - { type: 'split', cells: [c1, c2, c3] }
//       3-cell row, each cell is either null (empty) or
//       { node, fill?, arrowBelow? }
const PHASES = [
  {
    id: 'page-1',
    title: 'Page 1 of 3',
    rows: [
      { type: 'single', node: { id: 'vessel-unloading', title: 'Vessel Unloading', description: '', links: ['liftingtransport', 'ForkLiftTruckDoc', 'AccessingTrailersDoc', 'SlingsChainsLiftingDoc', 'WorkplaceTransportDoc', 'ManualHandlingDoc', 'tank-movement'] } },
      { type: 'single', node: { id: 'vessel-holding-area', title: 'Vessel Holding area', description: '', links: ['ForkLiftTruckDoc', 'liftingtransport', 'WorkplaceTransportDoc', 'SlingsChainsLiftingDoc'] } },
      { type: 'single', node: { id: 'vessel-inventory', title: 'Vessel Inventory', description: '', links: ['VesselTaggingPolicyDoc', 'tank-movement'] } },
      { type: 'single', node: { id: 'vessel-identification', title: 'Vessel Identification', description: '', links: ['VesselTaggingPolicyDoc', 'wse', 'pre-inspection'] } },
      { type: 'single', node: { id: 'tagging', title: 'Tagging', description: '', links: ['VesselTaggingPolicyDoc'] } },
      {
        type: 'split',
        cells: [
          {
            node: { id: 'nothing-required', title: 'Nothing Required', description: '', links: ['pre-inspection', 'VesselTaggingPolicyDoc'] },
            fill: 'orange',
            arrowBelow: false,
          },
          {
            node: { id: 'pre-inspection', title: 'Pre Inspection', description: '', links: ['vesselprep', 'wse', 'pre-inspection', 'hsafety', 'OnSiteRA'] },
            fill: 'green',
            arrowBelow: true,
          },
          {
            node: { id: 'vessel-scrapped', title: 'Vessel Scrapped', description: '', links: ['vessel-failure-report', 'VesselTaggingPolicyDoc'] },
            fill: 'red',
            arrowBelow: true,
          },
        ],
      },
      {
        type: 'split',
        cells: [
          null,
          { type: 'passthrough', arrowBelow: true },
          {
            node: { id: 'vessel-failure-report-submitted', title: 'Vessel Failure Report Submitted', description: '', links: ['vessel-failure-report', 'customer-complaint'] },
            fill: 'red',
            arrowBelow: false,
          },
        ],
      },
      {
        type: 'single',
        node: { id: 'vessel-vapour-removal', title: 'Vessel Vapour Removal', description: '', links: ['vesselprep', 'PortableCompressorUnitDoc', 'wse', 'FireGeneralSafetyDoc', 'COSHH'] },
        arrowBelow: true,
      },
    ],
  },
  {
    id: 'page-2',
    title: 'Page 2 of 3',
    rows: [
      { type: 'single', node: { id: 'vessel-valve-removal', title: 'Vessel Valve Removal', description: '', links: ['vesselprep', 'wse', 'PortableHandToolsDoc'] } },
      { type: 'single', node: { id: 'vessel-stud-fix-replacement', title: 'Vessel Stud Fix/Replacement', description: '', links: ['vesselprep', 'WeldingDoc', 'BenchGrinderDoc', 'PillarDrillDoc', 'PortableHandToolsDoc', 'wse'] } },
      {
        type: 'split',
        cells: [
          {
            node: { id: 'vessel-surface-powder-coated', title: 'Vessel Surface Powder Coated', description: '', links: ['spraybooth', 'COSHH', 'pre-coating-batch', 'paint-quality-inspection'] },
            fill: 'purple',
            arrowBelow: true,
          },
          {
            node: { id: 'vessel-coating-wet-sprayed', title: 'Vessel Coating Wet Sprayed', description: '', links: ['spraybooth', 'COSHH', 'pre-coating-batch', 'paint-quality-inspection'] },
            arrowBelow: true,
          },
        ],
      },
      {
        type: 'split',
        cells: [
          {
            node: { id: 'pyrolysis-oven', title: 'Pyrolysis Oven', description: '', links: ['pryolysis', 'OvensDoc', 'LoneWorkingDoc', 'FireGeneralSafetyDoc', 'COSHH'] },
            fill: 'purple',
            arrowBelow: true,
          },
          { type: 'passthrough', arrowBelow: true },
        ],
      },
      { type: 'single', node: { id: 'shot-blasting', title: 'Shot-blasting', description: '', links: ['shotblasting', 'LoneWorkingDoc', 'COSHH', 'PortableHandToolsDoc'] } },
      { type: 'single', node: { id: 'spray-booth', title: 'Spray booth', description: '', links: ['spraybooth', 'COSHH', 'paint-quality-inspection', 'pre-coating-batch'] } },
    ],
  },
  {
    id: 'page-3',
    title: 'Page 3 of 3',
    rows: [
      { type: 'single', node: { id: 'vessel-inspection', title: 'Vessel Inspection', description: '', links: ['vesselprep', 'vesselairtest', 'wse', 'engineering'] } },
      { type: 'single', node: { id: 'final-qc-checklist', title: 'Final QC Checklist', description: '', links: ['final-qc-checklist', 'engineering', 'wse'] } },
      { type: 'single', node: { id: 'ready-for-dispatch', title: 'Ready For Dispatch', description: '', links: ['liftingtransport', 'ForkLiftTruckDoc', 'tank-movement', 'tank-certification', 'AccessingTrailersDoc'] } },
    ],
  },
];

const TYPE_STYLES = {
  Policy: 'border-blue-400/40 bg-blue-500/10 text-blue-200',
  RAMS: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200',
  RA: 'border-amber-400/40 bg-amber-500/10 text-amber-200',
  SSOW: 'border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-200',
  Form: 'border-cyan-400/40 bg-cyan-500/10 text-cyan-200',
};

const TYPE_ICON = {
  Policy: ShieldCheck,
  RAMS: FileText,
  RA: FileText,
  SSOW: ClipboardList,
  Form: ClipboardList,
};

const ProcessNode = ({ node, fill, isOpen, onToggle, onNavigate, onResponsibilityClick }) => {
  const hasDescription = !!(node.description && node.description.trim());
  const hasLinks = Array.isArray(node.links) && node.links.length > 0;

  const fillStyle = fill && FILL_STYLES[fill];
  const containerClass = fillStyle
    ? `rounded-xl border overflow-hidden ${fillStyle.container}`
    : 'pvs-glass rounded-xl border border-pvs-accent/30 overflow-hidden';
  const headerClass = fillStyle
    ? `w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${fillStyle.header}`
    : 'w-full flex items-center justify-between gap-4 px-5 py-4 pvs-glass-hover text-left';

  return (
    <div className={containerClass}>
      <button
        type="button"
        onClick={onToggle}
        className={headerClass}
        title={isOpen ? 'Hide details' : 'Click for more information'}
      >
        <span className="text-sm sm:text-base font-semibold text-white text-center w-full">
          {node.title}
        </span>
        <Info
          className={`w-5 h-5 text-white flex-shrink-0 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-80'}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="border-t border-white/10 p-5 bg-black/20">
          <ul className="list-disc pl-5 space-y-4 text-pvs-text-muted">
            <li>
              <span className="text-white font-semibold">Responsibility: </span>
              <button
                type="button"
                onClick={() =>
                  onResponsibilityClick &&
                  onResponsibilityClick({ processId: node.id, processTitle: node.title })
                }
                className="text-pvs-accent hover:text-pvs-accent-warm underline underline-offset-2 font-medium"
                title={`See personnel responsible for ${node.title}`}
              >
                View Responsible Personnel
              </button>
            </li>

            <li>
              <span className="text-white font-semibold">Relevant Documentation: </span>
              {hasLinks ? (
                <ul className="list-[circle] pl-5 mt-2 space-y-2">
                  {node.links.map((key) => {
                    const meta = DOC_LIBRARY[key];
                    if (!meta) return null;
                    const allowed = canAccess(key);
                    const TypeIcon = TYPE_ICON[meta.type] || FileText;
                    const baseStyle = `inline-flex items-center justify-between gap-3 px-3 py-2 rounded-md border ${TYPE_STYLES[meta.type] || ''}`;
                    if (!allowed) {
                      return (
                        <li key={key}>
                          <span
                            className={`${baseStyle} opacity-60 cursor-not-allowed`}
                            title="You do not have permission to view this document"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <TypeIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="text-sm font-medium">{meta.label}</span>
                            </span>
                            <Lock className="w-4 h-4 flex-shrink-0" />
                          </span>
                        </li>
                      );
                    }
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => onNavigate(key, meta.target)}
                          className={`${baseStyle} pvs-glass-hover text-left`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <TypeIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm font-medium">{meta.label}</span>
                          </span>
                          <span className="text-xs uppercase tracking-wide opacity-80 flex-shrink-0">
                            {meta.type}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <em className="text-pvs-text-muted">Awaiting input</em>
              )}
            </li>

            <li>
              <span className="text-white font-semibold">Description: </span>
              {hasDescription ? (
                <span>{node.description}</span>
              ) : (
                <em className="text-pvs-text-muted">Awaiting input</em>
              )}
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const SingleArrow = () => (
  <div className="flex justify-center" aria-hidden="true">
    <ArrowDown className="w-5 h-5 text-pvs-accent/60" />
  </div>
);

// Shown only on real touch devices held in portrait. Asks the user to rotate
// because the process map's branching layout does not collapse cleanly into
// a narrow portrait column.
const MobileLandscapePrompt = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const query = '(max-width: 767px) and (orientation: portrait) and (pointer: coarse)';
    const mql = window.matchMedia(query);
    const update = () => setShow(mql.matches);
    update();
    if (mql.addEventListener) {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    // Safari < 14 fallback
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label="Rotate your device"
      className="fixed inset-0 z-[80] bg-pvs-bg/95 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center"
    >
      <div className="relative mb-6">
        <Smartphone
          className="w-24 h-24 text-pvs-accent"
          style={{ transform: 'rotate(-90deg)' }}
          aria-hidden="true"
        />
        <RotateCw
          className="w-10 h-10 text-pvs-accent-warm absolute -top-3 -right-3 animate-spin"
          style={{ animationDuration: '3s' }}
          aria-hidden="true"
        />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">Please rotate your device</h2>
      <p className="text-pvs-text-muted max-w-xs">
        The Vessel Refurbishment Process Map is best viewed in landscape on mobile. Turn your device sideways to continue.
      </p>
    </div>
  );
};

const ResponsibilityModal = ({ info, onClose }) => {
  useEffect(() => {
    if (!info) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [info, onClose]);

  if (!info) return null;

  const { processTitle, personnel } = info;
  const hasNames = Array.isArray(personnel) && personnel.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="pvs-glass border border-pvs-accent/40 rounded-xl shadow-pvs-glow w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4 gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5 text-pvs-accent flex-shrink-0" />
              <h3 className="text-xl font-bold text-white truncate">Responsible Personnel</h3>
            </div>
            <p className="text-sm text-pvs-text-muted">
              For <span className="text-white font-medium">{processTitle}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 rounded-full pvs-glass pvs-glass-hover flex items-center justify-center text-white"
            title="Close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {hasNames ? (
          <ul className="space-y-2">
            {personnel.map((person, i) => (
              <li
                key={`${person.name}-${i}`}
                className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 px-3 py-2 rounded-md bg-white/5 border border-pvs-border"
              >
                <span className="text-white font-semibold">{person.name}</span>
                <span className="text-pvs-text-muted text-sm">{person.jobTitle}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-lg border border-pvs-border bg-white/5 p-4 text-sm text-pvs-text-muted space-y-2">
            <p>No personnel have been recorded for this process step yet.</p>
            <p>
              This list is maintained from the <span className="text-white font-medium">PVS Training Matrix</span> knowledge base. Names and job titles will appear here automatically once that matrix is populated.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const VesselRefurbishmentProcessMap = ({ onReturnHome, onNavigateToDoc }) => {
  const [openId, setOpenId] = useState(null);
  const [responsibilityModal, setResponsibilityModal] = useState(null);

  // Always land at the top of the map, even if the user was scrolled deep
  // into the previous page (e.g. the Documents menu) before opening it.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  const handleNavigate = (key, target) => {
    if (typeof onNavigateToDoc === 'function') {
      onNavigateToDoc(key, target);
    }
  };

  const handleResponsibilityClick = ({ processId, processTitle }) => {
    setResponsibilityModal({
      processId,
      processTitle,
      personnel: getResponsiblePersonnel(processId),
    });
  };

  const closeResponsibilityModal = () => setResponsibilityModal(null);

  const renderRow = (row, rowIdx, isLastRow) => {
    if (row.type === 'single') {
      const arrowBelow = row.arrowBelow !== undefined ? row.arrowBelow : !isLastRow;
      return (
        <React.Fragment key={row.node.id}>
          <ProcessNode
            node={row.node}
            fill={row.fill}
            isOpen={openId === row.node.id}
            onToggle={() => toggle(row.node.id)}
            onNavigate={handleNavigate}
            onResponsibilityClick={handleResponsibilityClick}
          />
          {arrowBelow && <SingleArrow />}
        </React.Fragment>
      );
    }
    if (row.type === 'split') {
      const anyArrowBelow = row.cells.some((c) => c && c.arrowBelow);
      // Static class names so Tailwind's JIT can detect them. Add more entries
      // here if a future split row needs a different number of cells.
      const gridColsClass =
        row.cells.length === 2 ? 'grid-cols-2'
        : row.cells.length === 4 ? 'grid-cols-4'
        : 'grid-cols-3';
      return (
        <React.Fragment key={`split-${rowIdx}`}>
          <div className={`grid ${gridColsClass} gap-3 sm:gap-4 items-stretch`}>
            {row.cells.map((cell, ci) => (
              <div key={ci} className="min-w-0 flex">
                {cell && cell.type === 'passthrough' ? (
                  <div className="flex-1 flex justify-center">
                    <div className="w-0.5 bg-pvs-accent/60 h-full min-h-[3rem]" />
                  </div>
                ) : cell && cell.node ? (
                  <div className="flex-1">
                    <ProcessNode
                      node={cell.node}
                      fill={cell.fill}
                      isOpen={openId === cell.node.id}
                      onToggle={() => toggle(cell.node.id)}
                      onNavigate={handleNavigate}
                      onResponsibilityClick={handleResponsibilityClick}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          {anyArrowBelow && (
            <div className={`grid ${gridColsClass} gap-3 sm:gap-4`} aria-hidden="true">
              {row.cells.map((cell, ci) => (
                <div key={ci} className="flex justify-center">
                  {cell && cell.arrowBelow ? (
                    <ArrowDown className="w-5 h-5 text-pvs-accent/60" />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </React.Fragment>
      );
    }
    return null;
  };

  return (
    <PVSPageLayout className="py-12 px-4">
      <MobileLandscapePrompt />
      <div className="max-w-5xl mx-auto pvs-glass border border-pvs-accent/30 rounded-xl shadow-pvs-glow">
        <div className="border-b border-pvs-border p-6">
          <div className="flex justify-between items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">
              Vessel Refurbishment Process Map
            </h1>
            <DocumentArchiveButton />
            <button
              className="flex items-center justify-center w-12 h-12 flex-shrink-0 pvs-glass pvs-glass-hover rounded-xl"
              onClick={onReturnHome}
              title="Return to Documents"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-8">
          <table
            className="w-full border-collapse mb-2"
            style={{ borderColor: '#0437F2' }}
          >
            <tbody>
              <tr>
                <th className="text-white text-left py-2 px-4 border font-semibold w-1/3" style={{ backgroundColor: '#0437F2', borderColor: '#0437F2' }}>Document Reference</th>
                <td className="py-2 px-4 border text-pvs-text-muted" style={{ borderColor: '#0437F2' }}>PVS-VRPM-001</td>
              </tr>
              <tr>
                <th className="text-white text-left py-2 px-4 border font-semibold" style={{ backgroundColor: '#0437F2', borderColor: '#0437F2' }}>Document Title</th>
                <td className="py-2 px-4 border text-pvs-text-muted" style={{ borderColor: '#0437F2' }}>Vessel Refurbishment Process Map</td>
              </tr>
              <tr>
                <th className="text-white text-left py-2 px-4 border font-semibold" style={{ backgroundColor: '#0437F2', borderColor: '#0437F2' }}>Prepared by</th>
                <td className="py-2 px-4 border text-pvs-text-muted" style={{ borderColor: '#0437F2' }}>Pressure Vessel Services Limited</td>
              </tr>
              <tr>
                <th className="text-white text-left py-2 px-4 border font-semibold" style={{ backgroundColor: '#0437F2', borderColor: '#0437F2' }}>Approved by</th>
                <td className="py-2 px-4 border text-pvs-text-muted" style={{ borderColor: '#0437F2' }}>Board of Directors</td>
              </tr>
            </tbody>
          </table>

          <CalorVesselsNotice />

          <p className="text-pvs-text-muted leading-relaxed">
            Click the <Info className="inline w-4 h-4 align-text-bottom" aria-hidden="true" /> icon on any process step to expand it. Each step will show the relevant RAMS, Risk Assessments, Safe Systems of Work, Policies and Forms, alongside the personnel responsible — populated as the information is confirmed.
          </p>

          {(() => {
            const allRows = PHASES.flatMap((p) => p.rows);
            return (
              <div className="space-y-3">
                {allRows.map((row, idx) =>
                  renderRow(row, idx, idx === allRows.length - 1)
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <ResponsibilityModal info={responsibilityModal} onClose={closeResponsibilityModal} />
    </PVSPageLayout>
  );
};

export default VesselRefurbishmentProcessMap;
